import { Injectable } from '@angular/core';
import { CalendarEvent } from 'angular-calendar';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarWeeks,
  differenceInCalendarYears,
  endOfMonth,
  getDaysInMonth,
  isBefore,
  isSameDay,
  max as maxDate,
  min as minDate,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import {
  BusinessCalendarEvent,
  CalendarMonth,
  CalendarRecurrenceType,
  CalendarWeekday,
  Recurrence,
  WorkingDaysResponse,
} from '../../../../core/common/models/calendar.model';

/** Internal normalized form used by all expansion paths */
interface NormalizedRecurrence {
  type: CalendarRecurrenceType;
  interval: number;
  by_weekday: CalendarWeekday[] | null;
  by_monthday: number[] | null;
  by_month: CalendarMonth[] | null;
  by_setpos: number[] | null;
}

const EVENT_MONTHLY_BY_MONTHS = 'months';

const WEEKDAY_INDEX: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

const MONTH_INDEX: Record<string, number> = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

@Injectable({
  providedIn: 'root',
})
export class CalendarHelperService {
  formatDatetimeLocal(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  getSegmentBgClass(date: Date, workingDays: WorkingDaysResponse): string {
    if (!workingDays?.days || workingDays.days.length === 0) {
      return 'bg-white';
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    const config = workingDays.days.find(
      (d) => d.name?.value?.toLowerCase() === dayName
    );

    if (!config?.is_working) {
      return 'bg-destructive-50!';
    }

    // It's a working day. Check working hours.
    const [startH, startM] = (config.started_at || '00:00')
      .split(':')
      .map(Number);
    const [endH, endM] = (config.ended_at || '23:59').split(':').map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const segmentMinutes = date.getHours() * 60 + date.getMinutes();

    if (segmentMinutes >= startMinutes && segmentMinutes < endMinutes) {
      return 'bg-white';
    }

    return 'bg-neutral-50!';
  }

  getWeekStartIndex(workingDay: string): number {
    return WEEKDAY_INDEX[workingDay.toLowerCase()] || 0;
  }

  mapToCalendarEvent(event: BusinessCalendarEvent, occurrenceStart?: Date, occurrenceEnd?: Date): CalendarEvent {
    const start = occurrenceStart ?? new Date(event.started_at);
    const end = occurrenceEnd ?? new Date(event.ended_at);

    return {
      id: event.id,
      start,
      end,
      title: event.title,
      meta: {
        assignees: event.assignees,
        color: event.color?.value || null,
        source_type: event.source_type,
        isRecurring: !!event.recurrence,
        elementId: `calendar-event-${event.id}-${start.getTime()}`,
      },
    };
  }

  expandEventsForPeriod(
    businessEvents: BusinessCalendarEvent[],
    periodStart: Date,
    periodEnd: Date
  ): CalendarEvent[] {
    return businessEvents.flatMap((event) => {
      if (!event.recurrence) {
        return [this.mapToCalendarEvent(event)];
      }

      return this.expandRecurringEvent(event, periodStart, periodEnd);
    });
  }

  private expandRecurringEvent(
    event: BusinessCalendarEvent,
    periodStart: Date,
    periodEnd: Date
  ): CalendarEvent[] {
    const recurrence = event.recurrence;
    if (!recurrence) {
      return [];
    }

    const normalized = this.adaptRecurrence(recurrence);
    const eventStart = new Date(event.started_at);
    const eventEnd = new Date(event.ended_at);
    const durationMs = this.getOccurrenceDurationMs(eventStart, eventEnd);
    const { type, interval } = normalized;

    const seriesStart = eventStart;
    const lastOccurrenceStart = this.withEventTime(eventEnd, eventStart);

    const rangeStart = maxDate([periodStart, seriesStart]);
    const rangeEnd = minDate([periodEnd, lastOccurrenceStart]);

    if (isBefore(rangeEnd, rangeStart)) {
      return [];
    }

    if (type === 'weekly') {
      return this.expandWeeklyRecurringEvent(
        event,
        seriesStart,
        rangeStart,
        rangeEnd,
        durationMs,
        interval,
        normalized
      );
    }

    if (type === 'monthly' || type === 'yearly') {
      return this.expandMonthlyRecurringEvent(
        event,
        seriesStart,
        rangeStart,
        rangeEnd,
        durationMs,
        normalized,
        type
      );
    }

    let occurrenceStart = this.getFirstOccurrenceOnOrAfter(
      eventStart,
      rangeStart,
      type,
      interval
    );

    const occurrences: CalendarEvent[] = [];

    while (occurrenceStart.getTime() <= rangeEnd.getTime()) {
      if (occurrenceStart.getTime() >= rangeStart.getTime()) {
        occurrences.push(
          this.mapToCalendarEvent(
            event,
            occurrenceStart,
            new Date(occurrenceStart.getTime() + durationMs)
          )
        );
      }

      occurrenceStart = this.addRecurrenceInterval(occurrenceStart, type, interval);
    }

    return occurrences;
  }

  /**
   * Translates the API `Recurrence` shape into the normalized internal form
   * used by all expansion helpers.
   */
  private adaptRecurrence(r: Recurrence): NormalizedRecurrence {
    const type = (r.type?.value ?? 'daily') as CalendarRecurrenceType;
    const interval = Math.max(r.interval || 1, 1);
    const isByMonths = r.by?.value === EVENT_MONTHLY_BY_MONTHS;

    return {
      type,
      interval,
      by_weekday: r.days_of_week?.length ? r.days_of_week : null,
      by_monthday: r.days_of_month?.length ? r.days_of_month : null,
      by_month: isByMonths && r.months?.length ? r.months : null,
      by_setpos: r.relative_option != null ? [r.relative_option] : null,
    };
  }

  private expandWeeklyRecurringEvent(
    event: BusinessCalendarEvent,
    seriesStart: Date,
    rangeStart: Date,
    rangeEnd: Date,
    durationMs: number,
    interval: number,
    normalized: NormalizedRecurrence
  ): CalendarEvent[] {
    const eventStart = new Date(event.started_at);
    const byWeekdays = this.normalizeByWeekdays(
      normalized.by_weekday ?? undefined,
      eventStart
    );
    const occurrences: CalendarEvent[] = [];
    let cursor = startOfDay(rangeStart);

    while (cursor.getTime() <= rangeEnd.getTime()) {
      if (byWeekdays.has(cursor.getDay())) {
        const weeksFromSeriesStart = differenceInCalendarWeeks(cursor, seriesStart, {
          weekStartsOn: 0,
        });

        if (
          weeksFromSeriesStart >= 0 &&
          weeksFromSeriesStart % interval === 0
        ) {
          const occurrenceStart = this.withEventTime(cursor, eventStart);

          if (
            occurrenceStart.getTime() >= rangeStart.getTime() &&
            occurrenceStart.getTime() <= rangeEnd.getTime() &&
            occurrenceStart.getTime() >= seriesStart.getTime()
          ) {
            occurrences.push(
              this.mapToCalendarEvent(
                event,
                occurrenceStart,
                new Date(occurrenceStart.getTime() + durationMs)
              )
            );
          }
        }
      }

      cursor = addDays(cursor, 1);
    }

    return occurrences;
  }

  private expandMonthlyRecurringEvent(
    event: BusinessCalendarEvent,
    seriesStart: Date,
    rangeStart: Date,
    rangeEnd: Date,
    durationMs: number,
    normalized: NormalizedRecurrence,
    type: 'monthly' | 'yearly' = 'monthly'
  ): CalendarEvent[] {
    const { interval } = normalized;
    const eventStart = new Date(event.started_at);
    const byWeekdays = normalized.by_weekday?.length
      ? this.normalizeByWeekdays(normalized.by_weekday, eventStart)
      : null;
    const bySetpos = this.normalizeBySetpos(normalized.by_setpos ?? undefined, eventStart, !!byWeekdays);
    const byMonths = this.normalizeByMonths(normalized.by_month ?? undefined, eventStart, type);
    const hasExplicitMonths = !!normalized.by_month?.length;
    const hasExplicitMonthdays = !!normalized.by_monthday?.length;

    // Default monthly/yearly: same calendar day as started_at, advanced with addMonths
    if (!byWeekdays && !hasExplicitMonthdays) {
      return this.expandSimpleMonthlyRecurringEvent(
        event,
        seriesStart,
        rangeStart,
        rangeEnd,
        durationMs,
        interval,
        type,
        byMonths,
        hasExplicitMonths
      );
    }

    const byMonthdays = this.normalizeByMonthdays(normalized.by_monthday ?? undefined, eventStart);
    const occurrences: CalendarEvent[] = [];
    let monthCursor = startOfMonth(rangeStart);
    const lastMonth = startOfMonth(rangeEnd);

    while (monthCursor.getTime() <= lastMonth.getTime()) {
      const monthNumber = monthCursor.getMonth() + 1;

      if (
        byMonths.has(monthNumber) &&
        this.isMonthInInterval(monthCursor, seriesStart, interval, type, hasExplicitMonths)
      ) {
        const occurrenceDates = byWeekdays
          ? this.getMonthlyWeekdayOccurrences(monthCursor, byWeekdays, bySetpos)
          : this.getMonthlyMonthdayOccurrences(monthCursor, byMonthdays);

        for (const date of occurrenceDates) {
          const occurrenceStart = this.withEventTime(date, eventStart);

          if (this.isOccurrenceInRange(occurrenceStart, seriesStart, rangeStart, rangeEnd)) {
            occurrences.push(
              this.mapToCalendarEvent(
                event,
                occurrenceStart,
                new Date(occurrenceStart.getTime() + durationMs)
              )
            );
          }
        }
      }

      monthCursor = addMonths(monthCursor, 1);
    }

    return occurrences;
  }

  /**
   * Monthly/yearly on the started_at day-of-month until ended_at.
   * Preserves the original day-of-month (clamped for shorter months).
   */
  private expandSimpleMonthlyRecurringEvent(
    event: BusinessCalendarEvent,
    seriesStart: Date,
    rangeStart: Date,
    rangeEnd: Date,
    durationMs: number,
    interval: number,
    type: 'monthly' | 'yearly',
    byMonths: Set<number>,
    hasExplicitMonths: boolean
  ): CalendarEvent[] {
    const occurrences: CalendarEvent[] = [];
    const originalDay = seriesStart.getDate();
    let occurrenceStart = new Date(seriesStart);

    // Fast-forward close to the visible range
    if (occurrenceStart.getTime() < rangeStart.getTime()) {
      const monthsDiff = Math.max(
        0,
        differenceInCalendarMonths(rangeStart, occurrenceStart)
      );
      const skips = Math.floor(monthsDiff / interval) * interval;
      if (skips > 0) {
        occurrenceStart = this.addMonthsPreservingDay(occurrenceStart, skips, originalDay);
      }

      while (occurrenceStart.getTime() < rangeStart.getTime()) {
        occurrenceStart = this.addMonthsPreservingDay(
          occurrenceStart,
          hasExplicitMonths ? 1 : interval,
          originalDay
        );
      }
    }

    while (occurrenceStart.getTime() <= rangeEnd.getTime()) {
      const monthNumber = occurrenceStart.getMonth() + 1;

      if (
        byMonths.has(monthNumber) &&
        this.isMonthInInterval(
          startOfMonth(occurrenceStart),
          seriesStart,
          interval,
          type,
          hasExplicitMonths
        ) &&
        this.isOccurrenceInRange(occurrenceStart, seriesStart, rangeStart, rangeEnd)
      ) {
        occurrences.push(
          this.mapToCalendarEvent(
            event,
            occurrenceStart,
            new Date(occurrenceStart.getTime() + durationMs)
          )
        );
      }

      // When filtering by specific months, step one month at a time and let interval apply yearly
      occurrenceStart = this.addMonthsPreservingDay(
        occurrenceStart,
        hasExplicitMonths ? 1 : interval,
        originalDay
      );
    }

    return occurrences;
  }

  private addMonthsPreservingDay(date: Date, months: number, originalDay: number): Date {
    const next = new Date(date);
    const targetMonthIndex = next.getMonth() + months;
    next.setMonth(targetMonthIndex, 1);
    next.setDate(Math.min(originalDay, getDaysInMonth(next)));
    next.setHours(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds()
    );
    return next;
  }

  private isOccurrenceInRange(
    occurrenceStart: Date,
    seriesStart: Date,
    rangeStart: Date,
    rangeEnd: Date
  ): boolean {
    return (
      occurrenceStart.getTime() >= seriesStart.getTime() &&
      occurrenceStart.getTime() >= rangeStart.getTime() &&
      occurrenceStart.getTime() <= rangeEnd.getTime()
    );
  }

  private isMonthInInterval(
    monthCursor: Date,
    seriesStart: Date,
    interval: number,
    type: 'monthly' | 'yearly',
    hasExplicitMonths: boolean
  ): boolean {
    // Specific months (by_month) use a year-based interval so Jan/Jun aren't skipped
    if (type === 'yearly' || hasExplicitMonths) {
      const yearsFromSeriesStart =
        monthCursor.getFullYear() - seriesStart.getFullYear();
      return yearsFromSeriesStart >= 0 && yearsFromSeriesStart % interval === 0;
    }

    const monthsFromSeriesStart = differenceInCalendarMonths(
      monthCursor,
      startOfMonth(seriesStart)
    );

    return monthsFromSeriesStart >= 0 && monthsFromSeriesStart % interval === 0;
  }

  private normalizeByMonths(
    byMonth: CalendarMonth[] | undefined,
    eventStart: Date,
    type: 'monthly' | 'yearly'
  ): Set<number> {
    if (!byMonth?.length) {
      // Yearly without by_month defaults to the event's month only
      if (type === 'yearly') {
        return new Set([eventStart.getMonth() + 1]);
      }
      // Monthly without by_month = all months
      return new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    }

    const months = byMonth
      .map((month) => this.toMonthNumber(month))
      .filter((month): month is number => month !== null);

    return new Set(
      months.length ? months : [eventStart.getMonth() + 1]
    );
  }

  private toMonthNumber(month: CalendarMonth): number | null {
    if (typeof month === 'number' && month >= 1 && month <= 12) {
      return month;
    }

    if (typeof month === 'string') {
      const index = MONTH_INDEX[month.toLowerCase()];
      return index ?? null;
    }

    return null;
  }

  private getMonthlyMonthdayOccurrences(month: Date, byMonthdays: number[]): Date[] {
    const daysInMonth = getDaysInMonth(month);

    return byMonthdays
      .filter((day) => day >= 1 && day <= daysInMonth)
      .sort((a, b) => a - b)
      .map((day) => new Date(month.getFullYear(), month.getMonth(), day));
  }

  private getMonthlyWeekdayOccurrences(
    month: Date,
    byWeekdays: Set<number>,
    bySetpos: number[]
  ): Date[] {
    const matchingDays: Date[] = [];
    let cursor = startOfMonth(month);
    const monthEnd = endOfMonth(month);

    while (cursor.getTime() <= monthEnd.getTime()) {
      if (byWeekdays.has(cursor.getDay())) {
        matchingDays.push(new Date(cursor));
      }
      cursor = addDays(cursor, 1);
    }

    if (!matchingDays.length) {
      return [];
    }

    const selected = new Set<number>();

    for (const setpos of bySetpos) {
      if (setpos === 0) {
        continue;
      }

      const index = setpos > 0 ? setpos - 1 : matchingDays.length + setpos;
      if (index >= 0 && index < matchingDays.length) {
        selected.add(index);
      }
    }

    return [...selected]
      .sort((a, b) => a - b)
      .map((index) => matchingDays[index]);
  }

  private normalizeByMonthdays(
    byMonthday: number[] | undefined,
    eventStart: Date
  ): number[] {
    if (!byMonthday?.length) {
      return [eventStart.getDate()];
    }

    const days = [...new Set(
      byMonthday.filter((day) => Number.isInteger(day) && day >= 1 && day <= 31)
    )].sort((a, b) => a - b);

    return days.length ? days : [eventStart.getDate()];
  }

  private normalizeBySetpos(
    bySetpos: number[] | undefined,
    eventStart: Date,
    hasWeekdays: boolean
  ): number[] {
    if (!hasWeekdays) {
      return [];
    }

    if (bySetpos?.length) {
      return [...new Set(bySetpos.filter((pos) => pos !== 0))];
    }

    // Default to the same nth weekday as the event start (e.g. 2nd Monday)
    return [Math.ceil(eventStart.getDate() / 7)];
  }

  private normalizeByWeekdays(
    byWeekday: CalendarWeekday[] | undefined,
    eventStart: Date
  ): Set<number> {
    if (!byWeekday?.length) {
      return new Set([eventStart.getDay()]);
    }

    const weekdays = byWeekday
      .map((day) => this.toWeekdayIndex(day))
      .filter((day): day is number => day !== null);

    return new Set(weekdays.length ? weekdays : [eventStart.getDay()]);
  }

  private toWeekdayIndex(day: CalendarWeekday): number | null {
    if (typeof day === 'number' && day >= 1 && day <= 7) {
      // API uses 1=Mon…7=Sun; JS Date.getDay() uses 0=Sun…6=Sat
      return day === 7 ? 0 : day;
    }

    if (typeof day === 'string') {
      const index = WEEKDAY_INDEX[day.toLowerCase()];
      if (index === undefined) return null;
      return index === 7 ? 0 : index;
    }

    return null;
  }

  private getFirstOccurrenceOnOrAfter(
    eventStart: Date,
    rangeStart: Date,
    type: CalendarRecurrenceType,
    interval: number
  ): Date {
    if (eventStart.getTime() >= rangeStart.getTime()) {
      return new Date(eventStart);
    }

    let skips = 0;

    switch (type) {
      case 'weekly':
        skips = Math.floor(
          differenceInCalendarWeeks(rangeStart, eventStart) / interval
        );
        break;
      case 'monthly':
        skips = Math.floor(
          differenceInCalendarMonths(rangeStart, eventStart) / interval
        );
        break;
      case 'yearly':
        skips = Math.floor(
          differenceInCalendarYears(rangeStart, eventStart) / interval
        );
        break;
      case 'daily':
      default:
        skips = Math.floor(
          differenceInCalendarDays(rangeStart, eventStart) / interval
        );
        break;
    }

    let occurrence = this.addRecurrenceInterval(eventStart, type, skips * interval);

    while (occurrence.getTime() < rangeStart.getTime()) {
      occurrence = this.addRecurrenceInterval(occurrence, type, interval);
    }

    return occurrence;
  }

  private addRecurrenceInterval(
    date: Date,
    type: CalendarRecurrenceType,
    interval: number
  ): Date {
    switch (type) {
      case 'weekly':
        return addWeeks(date, interval);
      case 'monthly':
        return addMonths(date, interval);
      case 'yearly':
        return addYears(date, interval);
      case 'daily':
      default:
        return addDays(date, interval);
    }
  }

  private getOccurrenceDurationMs(eventStart: Date, eventEnd: Date): number {
    // First occurrence is on one day: use the real start/end duration
    if (isSameDay(eventStart, eventEnd)) {
      return Math.max(eventEnd.getTime() - eventStart.getTime(), 60_000);
    }

    // Series spans multiple days: duration is time-of-day between start and end
    const startTimeMs = this.getTimeOfDayMs(eventStart);
    const endTimeMs = this.getTimeOfDayMs(eventEnd);
    const durationMs = endTimeMs - startTimeMs;

    // Same clock time on the series end date — default to 1 hour (don't fill the whole day)
    if (durationMs <= 0) {
      return 60 * 60 * 1000;
    }

    return durationMs;
  }

  private getTimeOfDayMs(date: Date): number {
    return (
      date.getHours() * 3_600_000 +
      date.getMinutes() * 60_000 +
      date.getSeconds() * 1_000 +
      date.getMilliseconds()
    );
  }

  private withEventTime(date: Date, eventStart: Date): Date {
    const withTime = new Date(date);
    withTime.setHours(
      eventStart.getHours(),
      eventStart.getMinutes(),
      eventStart.getSeconds(),
      eventStart.getMilliseconds()
    );
    return withTime;
  }
}
